import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import pairedSilence from '@/assets/paired-silence.jpg';
import emergentForm from '@/assets/emergent-form.jpg';
import quietAlignment from '@/assets/quiet-alignment.jpg';
import dualHorizon from '@/assets/dual-horizon.jpg';
import clayVesselLight from '@/assets/clay-vessel-light.jpg';
import innerGlow from '@/assets/inner-glow.jpg';
import quietVessels from '@/assets/quiet-vessels.jpg';
import returningCurve from '@/assets/returning-curve.jpg';
import threadsOfGround from '@/assets/threads-of-ground.jpg';
import quietAssemblage from '@/assets/quiet-assemblage.jpg';

const Portfolio = () => {

  const portfolioItems = [
    {
      id: 1,
      title: 'Paired Silence',
      year: '2025',
      medium: 'Ceramic',
      dimensions: 'Approx. 30 × 14 × 12 cm',
      category: 'sculptural',
      image: pairedSilence,
      description: 'This sculptural pair reflects on coexistence and environmental imbalance, standing as quiet markers of shared vulnerability shaped by pressure, erosion, and climate change.',
    },
    {
      id: 2,
      title: 'Emergent Form',
      year: '2024',
      medium: 'Ceramic',
      dimensions: 'Small-scale sculptural work',
      category: 'sculptural',
      image: emergentForm,
      description: 'This small-scale sculptural work explores growth and transformation through an abstracted organic form. The upward tilt and asymmetry suggest emergence and movement, while the compact base anchors the piece in stillness. The work balances delicacy and solidity, inviting close viewing and quiet reflection on becoming rather than completion.',
    },
    {
      id: 3,
      title: 'Quiet Alignment',
      year: '2023',
      medium: 'Ceramic',
      dimensions: 'Paired sculptural work',
      category: 'sculptural',
      image: quietAlignment,
      description: 'This paired sculptural work explores relationship and proximity through two vertically oriented forms. Similar in structure yet distinct in scale, the pieces suggest dialogue rather than duplication. Their speckled surfaces and elongated profiles emphasise rhythm, balance, and subtle difference, inviting a slow reading of form and space.',
    },
    {
      id: 4,
      title: 'Dual Horizon',
      year: '2023',
      medium: 'Ceramic',
      dimensions: 'Sculptural work',
      category: 'sculptural',
      image: dualHorizon,
      description: 'This sculptural work explores balance and tension through mirrored curved forms stacked in opposition. The piece suggests two horizons meeting, where weight is distributed rather than resolved. The intersecting arches create a sense of suspension, inviting the viewer to consider equilibrium as a negotiated state rather than a fixed condition.',
    },
    {
      id: 5,
      title: 'Clay Vessel for Light',
      year: '2023',
      medium: 'Hand-built ceramic, electrical fitting, fabric shade',
      dimensions: 'Approx. 45 × 28 × 28 cm',
      category: 'sculptural',
      image: clayVesselLight,
      description: 'Clay Vessel for Light explores the meeting point between sculpture and function. Formed from clay, the work draws on the language of the vessel as both container and presence. Light transforms the ceramic body from a static object into an activated form, emphasising surface, weight, and quiet gesture. The piece invites contemplation of how utilitarian objects can also operate as sculptural experiences.',
    },
    {
      id: 6,
      title: 'Inner Glow',
      year: '2022',
      medium: 'Hand-built ceramic sculpture with earth-toned lamp and electrical fitting',
      dimensions: 'Approx. 40 × 30 × 15 cm',
      category: 'sculptural',
      image: innerGlow,
      description: 'Inner Glow explores the relationship between form and illumination through an earth-toned ceramic lamp that functions as both sculpture and vessel. The light emerges organically from the clay body, reinforcing a sense of warmth, grounding, and quiet presence. Surface textures and natural tonal variations emphasise tactility, inviting contemplation of light as something held, contained, and gently released.',
    },
    {
      id: 7,
      title: 'Quiet Vessels',
      year: '2022',
      medium: 'Hand-built ceramic vessels',
      dimensions: 'Variable (grouped work)',
      category: 'sculptural',
      image: quietVessels,
      description: 'Quiet Vessels brings together a small group of ceramic forms that explore restraint, repetition, and subtle variation. The muted surface tones and simple silhouettes emphasise function as a starting point rather than an end, allowing the vessels to sit between utility and sculpture. Seen together, the works suggest intimacy, domestic memory, and the quiet presence of handmade objects within everyday space.',
    },
    {
      id: 8,
      title: 'Returning Curve',
      year: '2022',
      medium: 'Hand-built ceramic sculpture',
      dimensions: 'Approx. 28 × 30 × 15 cm',
      category: 'sculptural',
      image: returningCurve,
      description: 'Returning Curve explores continuity and balance through a closed, looping ceramic form. The circular movement suggests return, pause, and quiet repetition, while the openings interrupt the solidity of the body, allowing space to pass through the work. The matte black surface absorbs light, emphasising mass and contour rather than decoration. Positioned between vessel and sculpture, the piece reflects an ongoing interest in form as a container for absence as much as presence.',
    },
    {
      id: 9,
      title: 'Threads of Ground',
      year: '2022',
      medium: 'Hand-formed ceramic beads, cotton fibre',
      dimensions: 'Variable (each strand approx. 85–95 cm)',
      category: 'sculptural',
      image: threadsOfGround,
      description: 'Threads of Ground explores repetition, rhythm, and touch through hand-formed ceramic beads combined with soft cotton fibre. The work draws on traditions of adornment and ritual objects while shifting them into a sculptural language. The contrast between fired clay and textile introduces a dialogue between permanence and fragility, weight and movement. Presented as an object rather than wearable jewellery, the piece invites contemplation of connection, continuity, and the quiet labour embedded in making.',
    },
    {
      id: 10,
      title: 'Quiet Assemblage',
      year: '2021',
      medium: 'Hand-built ceramic vessels',
      dimensions: 'Variable (largest approx. 30 × 28 × 28 cm)',
      category: 'sculptural',
      image: quietAssemblage,
      description: 'Quiet Assemblage brings together a group of hand-built ceramic forms that sit between utility and sculpture. Each vessel holds its own rhythm and proportion, while the grouping creates a shared visual language of restraint, balance, and softness. The natural clay surface is left largely untreated, allowing traces of making, pressure, and movement to remain visible. Presented collectively, the works function less as individual objects and more as a conversation about containment, presence, and stillness.',
    },
    {
      id: 11,
      title: 'Terracotta Forms',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: 'Variable (grouped work)',
      category: 'sculptural',
      image: '/__l5e/assets-v1/3fc18f28-e545-435d-a4f9-57f4158e4dc7/terracotta-forms.png',
      description: 'A gathering of hand-built ceramic forms in warm terracotta, cool grey, and cream. Each piece carries its own quiet posture, while together they create a dialogue of balance, proximity, and material contrast.',
    },
    {
      id: 12,
      title: 'Striped Vessel',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '16 × 11.5 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/ab38b32b-a674-4860-97cd-6e802aae5597/striped-vessel.png',
      description: 'An abstract hand-built vessel composed of bold vertical and horizontal bands. The interplay of charcoal, cream, and soft blue creates a rhythmic surface that sits between functional form and sculptural object.',
    },
    {
      id: 13,
      title: 'Dark Sculptural Form',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '8.5 × 6 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/7046da8e-1f99-4ea9-9558-d1527a5f7e23/dark-sculptural-form.png',
      description: 'A compact, dark-toned ceramic form with a single curved opening. Its speckled surface and leaning silhouette suggest movement held in stillness, inviting close viewing from every angle.',
    },
    {
      id: 14,
      title: 'Brown Bottle Vase',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '10 × 5.5 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/b071e55c-8f9a-40d5-9b2b-8176096dd540/brown-bottle-vase.png',
      description: 'A tall, hand-built bottle vase in muted brown clay. The subtle horizontal layering and restrained silhouette reflect an ongoing interest in vessels that exist between utility and quiet sculpture.',
    },
    {
      id: 15,
      title: 'Yellow Textured Vessel',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '8 × 11 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/cff4c4c4-7fa7-4ceb-8dad-e4ec21975335/yellow-textured-vessel.png',
      description: 'A vibrant yellow vessel with a heavily worked surface. The rough, gestural texture and asymmetrical rim celebrate the physical act of making, while the saturated colour gives the form a bold contemporary presence.',
    },
    {
      id: 16,
      title: 'Brown Abstract Vessel',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '8.5 × 8 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/60291dee-2b4c-446d-86f3-c3c73a4811fd/brown-abstract-vessel.png',
      description: 'An organic, hand-built vessel with rounded protrusions and a single circular opening. The soft brown surface and biomorphic shape blur the line between container and sculpture.',
    },
    {
      id: 17,
      title: 'Black Ring Vessel',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '16 inches height',
      category: 'sculptural',
      image: '/__l5e/assets-v1/8be80186-51a0-44b7-b861-03f8ea841cb6/black-ring-vessel.png',
      description: 'A striking black vessel formed as a continuous ring. The hollow centre allows space to pass through the clay body, creating a dialogue between mass, void, and silhouette.',
    },
    {
      id: 18,
      title: 'Yellow Angular Vessel',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '12 inches height',
      category: 'sculptural',
      image: '/__l5e/assets-v1/9e35a59b-55c5-4af3-bcdc-b39c53295c95/yellow-angular-vessel.png',
      description: 'A bright yellow vessel with clean, angular planes and a geometric neck. The bold colour and faceted surface give a contemporary edge to the ancient language of the vessel.',
    },
    {
      id: 19,
      title: 'Tan Sculptural Form',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '7 × 8 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/7f9ddee2-ed3f-4ce5-80c2-db1150e30b75/tan-sculptural-form.png',
      description: 'A low, sweeping tan form pierced by a circular opening. Its smooth surface and balanced asymmetry evoke wind-worn stone and the quiet movement of natural erosion.',
    },
    {
      id: 20,
      title: 'Yellow Speckled Form',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '12 × 5.5 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/f00460b0-b91a-4149-9a73-2ad164a223a8/yellow-speckled-form.png',
      description: 'A tall, biomorphic sculpture in warm yellow clay with scattered dark speckles. The stacked, rounded volumes suggest growth and quiet transformation frozen in ceramic.',
    },
    {
      id: 21,
      title: 'Blue Teardrop Sculpture',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '6.5 × 5 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/ba2c68bb-ba9f-44a8-8386-5c382b803429/blue-teardrop-sculpture.png',
      description: 'A smooth blue-grey teardrop form pierced at its centre. The soft, weathered surface and balanced proportions give the piece a calm, pebble-like presence.',
    },
    {
      id: 22,
      title: 'Cobalt Vessel',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '11 × 8.5 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/08230586-6dd9-4c73-90e4-4a94e6d43303/cobalt-vessel.png',
      description: 'A deep cobalt vessel with broad shoulders and a tapered neck. The rich, saturated colour and hand-built surface create a bold yet contemplative object.',
    },
    {
      id: 23,
      title: 'Grey Stone Form',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: '16 × 11.5 inches',
      category: 'sculptural',
      image: '/__l5e/assets-v1/bff8e1d8-1628-4dbd-8a15-28d20332ed21/grey-stone-form.png',
      description: 'A tall grey ceramic form with a single central opening and a textured, stone-like surface. The piece recalls weathered rock and ancient standing forms, reimagined through contemporary hand-building.',
    },
    {
      id: 24,
      title: 'Sculptural Family Group',
      year: '2025',
      medium: 'Hand-built ceramic',
      dimensions: 'Variable (grouped work)',
      category: 'sculptural',
      image: '/__l5e/assets-v1/4941eaa6-690e-461d-9d11-1aa1633dfd3b/sculptural-family-group.png',
      description: 'A gathering of hand-built ceramic figures in black, cream, grey, and blue. The clustered forms suggest relationship and conversation, each shape maintaining its own identity while contributing to a shared sculptural language.',
    },
  ];


  return (
    <div className="py-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-16">
        <div className="max-w-4xl mx-auto text-center text-reveal">
          <h1 className="font-display text-5xl md:text-6xl font-bold text-primary mb-6">
            Portfolio
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            A collection of ceramic works exploring the dialogue between tradition and innovation
          </p>
        </div>
      </section>


      {/* Portfolio Grid */}
      <section className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {portfolioItems.map((item, index) => (
            <Dialog key={item.id}>
              <DialogTrigger asChild>
                <Card className="hover-lift card-gradient border-border/50 cursor-pointer group animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="image-hover bg-cream/50">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-80 object-contain rounded-t-lg"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-semibold mb-2 group-hover:text-primary transition-smooth">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-1">{item.year}</p>
                    <p className="text-muted-foreground text-sm mb-2">{item.medium}</p>
                    <p className="text-muted-foreground text-sm">{item.dimensions}</p>
                  </div>
                </Card>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-cream/50 rounded-lg">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-96 object-contain rounded-lg"
                    />
                  </div>
                  <div className="space-y-4">
                    <h2 className="font-display text-3xl font-semibold text-primary">
                      {item.title}
                    </h2>
                    <div className="space-y-2">
                      <p><span className="font-medium">Year:</span> {item.year}</p>
                      <p><span className="font-medium">Medium:</span> {item.medium}</p>
                      <p><span className="font-medium">Dimensions:</span> {item.dimensions}</p>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </section>

    </div>
  );
};

export default Portfolio;
